/**
 * UN PARTIDO, ENTERO Y CON DIRECCIÓN PROPIA.
 *
 * QUÉ PROBLEMA RESUELVE. Un resultado se podía mirar en tres lados —el cuadro
 * del torneo, el historial del perfil, la Actividad— y en ninguno se podía
 * ABRIR. La foto del partido y lo que dijo la gente existían solo mientras esa
 * tarjeta siguiera en la primera página de un muro: a la semana siguiente no
 * había forma de volver a ella. Javier lo dijo así: «debería llevarme al
 * partido y a la foto, así como comentarios y reacciones».
 *
 * ES LA MISMA TARJETA DE LA ACTIVIDAD, sin nada agregado. No hay una
 * «pantalla del partido» con su propio dibujo: lo que la Actividad muestra en
 * una lista es exactamente lo que hay que ver de un partido, y hacer una
 * segunda versión sería la que se queda sin el arreglo la próxima vez. Lo
 * único que esta pantalla pone de su parte es el botón de volver.
 *
 * `torneo` O `reto` EN LA DIRECCIÓN, porque son dos tablas: ver
 * `rutaDelPartido`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { ActivityEntry, MatchComment, MatchScore } from '@tennisup/contracts';

import { ActivityCard } from '../components/ActivityCard';
import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { comentarEn, reaccionarEn } from '../lib/activityActions';
import { errorMessage } from '../lib/errorMessage';

export function MatchPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { kind = 'torneo', id = '' } = useParams();
  const [entrada, setEntrada] = useState<ActivityEntry | null>(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setEntrada(kind === 'reto' ? await api.challengeCard(id) : await api.matchCard(id));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      // `listo` APARTE DE `entrada`: sin él, un partido que no se puede ver
      // deja la pantalla en «Cargando…» para siempre con el error arriba, y
      // no se entiende cuál de las dos cosas está pasando.
      setListo(true);
    }
  }, [kind, id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function comentar(tarjeta: ActivityEntry, body: string): Promise<void> {
    try {
      await comentarEn(tarjeta, body);
      await load();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * CORREGIR EL MARCADOR de un reto ya anotado. Ver `ActivitySection`, que
   * hace lo mismo: el orden del marcador no se toca acá.
   */
  async function corregirMarcador(
    tarjeta: ActivityEntry,
    score: MatchScore,
    winnerUserId: string | null,
    superTieBreak: boolean,
  ): Promise<void> {
    try {
      await api.setChallengeResult(tarjeta.id, { winnerUserId, score, superTieBreak });
      await load();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function borrarComentario(comentario: MatchComment): Promise<void> {
    try {
      await api.removeComment(comentario.id);
      await load();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * La reacción se aplica sola y no vuelve a pedir la tarjeta: acá sería una
   * sola consulta, pero el botón tiene que prenderse al toque igual que en la
   * lista — si en una pantalla tarda medio segundo y en la otra no, se siente
   * roto justo en la que tarda.
   */
  async function reaccionar(tarjeta: ActivityEntry, emoji: string, on: boolean): Promise<void> {
    try {
      const cuenta = await reaccionarEn(tarjeta, emoji, on);
      setEntrada((antes) => (antes === null ? null : { ...antes, reactions: cuenta }));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <AppLayout>
      {/*
        VUELVE AL CLUB CUANDO NO HAY «ATRÁS». Esta dirección es la que se pega
        en un chat, así que muy seguido es la PRIMERA pantalla de la sesión, y
        ahí `navigate(-1)` sacaría a la persona de la aplicación. El club del
        partido es el lugar de siempre; un amistoso no tiene ninguno, y
        entonces queda Retos, que es de donde se llega.
      */}
      <BackButton to={entrada?.communityId == null ? '/retos' : `/clubes/${entrada.communityId}`} />
      <h1>{t('match.title')}</h1>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {entrada === null ? (
        listo ? (
          error === null ? (
            <p className="help">{t('match.notFound')}</p>
          ) : null
        ) : (
          <p className="help">{t('common.loading')}</p>
        )
      ) : (
        <ActivityCard
          entrada={entrada}
          yoId={session?.user.id ?? ''}
          onComment={comentar}
          onDeleteComment={borrarComentario}
          onReact={reaccionar}
          onFixScore={corregirMarcador}
          permalink={false}
        />
      )}
    </AppLayout>
  );
}
